import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Button,
  Container,
  Grid,
} from '@mui/material';

import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';

// Theme Stuff / Theme Configs
import { getMuiTableBodyProps, getTableTheme } from '../../js/table_theme_config'; // Import the helpers

// Pure JS
import query_data from '../../js/query_data';
import downloadFile, { getFileSize } from '../../js/download_file';
import { convertEpochToDateTime, formatDuration } from '../../js/time_helpers';

// Component Imports
import RASDSTsFilterDialog from './RASDSTsFilterDialog';
import UploadRasDatasetDialog from './UploadRasDatasetDialog';
import { DownloadModalBase, ProgressSnackbarBase, FileSizeEntry } from '../common/DownloadModalBase';
import { useSearchParams } from 'react-router';

export type FileSizeMap = Record<string, FileSizeEntry>;

const RASDSTsTable = () => {
  // Data State & Table States
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);

  // Pagination state
  const [page, setPage] = useState(0); // 0-based for MRT
  const [pageSize, setPageSize] = useState(20);

  // Dialog States
  const [isRASDSTsFilterDialogOpen, setisRASDSTsFilterDialogOpen] = useState<boolean>(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);
  const [downloadModal, setDownloadModal] = useState<boolean>(false);
  const [progressToast, setProgressToast] = useState<boolean>(false);

  // File sizes state for download
  const [fileSizes, setFileSizes] = useState<FileSizeMap>({});

  const [searchParams, setSearchParams] = useSearchParams({
    software_version: [],
    source_dataset: [],
  });

  // Convert URLSearchParams to JSON for API
  function convertParamsToJSON(params) {
    const filters: any = {};
    const software_version = params.getAll('software_version');
    const source_dataset = params.getAll('source_dataset');

    if (software_version.length > 0) {
      filters.software_version = software_version;
    }
    if (source_dataset.length > 0) {
      filters.source_dataset = source_dataset;
    }
    return filters;
  }

  // Fetch and flatten data with pagination
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const filters = convertParamsToJSON(searchParams);

      const requestBody: any = {
        pagination: {
          page: page + 1,
          size: pageSize,
        },
      };

      if (Object.keys(filters).length > 0) {
        requestBody.filters = filters;
      }

      const json_list = await query_data("/rasdsts", requestBody, 'POST');
      setData(json_list.data);
      setTotal(json_list.total || 0);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [page, pageSize, searchParams]);

  const onSaveCallback = (requestBody) => {
    // Remove empty, null, undefined, or empty array values
    const cleaned = Object.fromEntries(
      Object.entries(requestBody).filter(([k, v]) => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== '' && v !== null && v !== undefined;
      })
    );
    // Convert all values to string or string[]
    const stringified = Object.fromEntries(
      Object.entries(cleaned).map(([k, v]) => {
        if (Array.isArray(v)) return [k, v.map(String)];
        return [k, String(v)];
      })
    );
    setSearchParams(stringified);
    setPage(0); // Reset to first page on filter
    setisRASDSTsFilterDialogOpen(false);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadSelected = async (selected) => {
    setProgressToast(true);

    const downloaders = selected.map(async (row) => {
      const bucket_name_file_path = row.original.bucket_name_archive_path;
      setFileSizes(prevSizes => ({
        ...prevSizes,
        [bucket_name_file_path]: {
          ...prevSizes[bucket_name_file_path],
          size: 0, // Reset size to 0 before download
          isDownloading: true,
        },
      }));
      // Download the file
      try {
        await downloadFile(bucket_name_file_path, (progress) => {
          // Update the file size and download progress in the state
          setFileSizes(prevSizes => ({
            ...prevSizes,
            [bucket_name_file_path]: {
              ...prevSizes[bucket_name_file_path],
              downloadProgress: progress,
            },
          }));
        });

        // Set isDownloading to false
        setFileSizes(prevSizes => ({
          ...prevSizes,
          [bucket_name_file_path]: {
            ...prevSizes[bucket_name_file_path],
            isDownloading: false,
            size: prevSizes[bucket_name_file_path].size, // Update with actual size if needed
          },
        }));
      } catch (err) {
        setFileSizes(prevSizes => ({
          ...prevSizes,
          [bucket_name_file_path]: {
            ...prevSizes[bucket_name_file_path],
            isDownloading: false,
            error: "Download failed.",
          },
        }));
        console.error("Download error:", err);
      }
    });
    Promise.all(downloaders)
      .then(() => {
        setProgressToast(false);
      }
      )
      .catch((err) => {
        console.error("Error during downloads:", err);
        setProgressToast(false);
      }
      );
  };

  // Flat columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
      },
      {
        accessorKey: 'version',
        header: 'Version',
        size: 40,
      },
      {
        accessorKey: 'software_version',
        header: 'Software Version',
        size: 80,
      },
      {
        accessorKey: 'source_dataset',
        header: 'Source Dataset',
        size: 140,
      },
      {
        accessorKey: 'subject_id',
        header: 'Subject',
        size: 80,
      },
      {
        accessorKey: 'transformation_type',
        header: 'Type',
        size: 20,
      },
      {
        accessorKey: 'transformation_config',
        header: 'Config',
        size: 20,
      },
      {
        accessorKey: 'transformation_time',
        header: 'Time',
        size: 140,
        Cell: ({ cell }) => {
          const timestamp = cell.getValue() as number;
          if (!timestamp) return '';
          const dt = convertEpochToDateTime(timestamp);
          return (
            <div>
              <div>{dt.date}</div>
              <div>{dt.time}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'duration_s',
        header: 'Duration',
        size: 80,
        Cell: ({ cell }) => formatDuration(cell.getValue() as number),
      },

      {
        accessorKey: 'bucket_name_archive_path',
        header: 'bucket_name_archive_path',
        size: 0,
        enableHiding: true,
        muiTableBodyCellProps: { style: { display: 'none' } },
        muiTableHeadCellProps: { style: { display: 'none' } },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableExpanding: false,
    state: { isLoading: loading, pagination: { pageIndex: page, pageSize } },
    manualPagination: true,
    rowCount: total,
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50, 100],
    },
    onPaginationChange: (updater) => {
      // updater can be a function or value
      const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize }) : updater;
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize);
        setPage(0); // Reset to first page if page size changes
      } else {
        setPage(next.pageIndex);
      }
    },
    muiTableBodyProps: {
      ...getMuiTableBodyProps(false),
      sx: { '& td': { padding: '0.5rem' } },
    },
    muiTableHeadCellProps: {
      sx: { padding: '0.5rem' },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflowY: 'auto',
      },
    },
    mrtTheme: getTableTheme(false),
    positionToolbarAlertBanner: 'bottom',
    getRowId: (row) => row.id,
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <Grid container justifyContent="flex-start">
          <Button onClick={fetchData}>Refresh</Button>
          <Button onClick={() => setisRASDSTsFilterDialogOpen(true)}>Set Filters</Button>
          <Button onClick={() => setIsUploadDialogOpen(true)}>Upload Mocap</Button>
          <Button
            disabled={table.getSelectedRowModel().flatRows.length === 0}
            onClick={() => {
              setDownloadModal(true);
              table.getSelectedRowModel().flatRows.forEach(async (row) => {
                const bucket_name_file_path = row.original.bucket_name_archive_path;
                await getFileSize(bucket_name_file_path).then((res) => {
                  setFileSizes(prevSizes => ({
                    ...prevSizes,
                    [bucket_name_file_path]: {
                      size: res,
                      downloadProgress: 0,
                      isDownloading: false,
                      error: null,
                    },
                  }));
                }).catch((err) => {
                  console.error("Error getting file size:", err);
                });
              });
            }}
          >
            Download
          </Button>
        </Grid>
      );
    },
  });

  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 2, width: '100%' }}>
      <RASDSTsFilterDialog
        open={isRASDSTsFilterDialogOpen}
        onClose={() => setisRASDSTsFilterDialogOpen(false)}
        onSaveCallback={onSaveCallback}
        searchParams={searchParams}
      />
      <UploadRasDatasetDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onUploadSuccess={() => {
          fetchData();
          setIsUploadDialogOpen(false);
        }}
      />
      <DownloadModalBase
        open={downloadModal}
        setOpen={setDownloadModal}
        selected={table.getSelectedRowModel().flatRows}
        fileSizes={fileSizes}
        setFileSizes={setFileSizes}
        handleDownloadSelected={handleDownloadSelected}
        getKey={row => row.original.bucket_name_archive_path}
        getName={row => row.original.name}
        title="Download Selected RAS Datasets"
        itemLabel="Datasets"
      />
      <ProgressSnackbarBase
        open={progressToast}
        setOpen={setProgressToast}
        selected={table.getSelectedRowModel().flatRows}
        fileSizes={fileSizes}
        setFileSizes={setFileSizes}
        getKey={row => row.original.bucket_name_archive_path}
        getName={row => row.original.name}
        headline="Downloading Files"
      />
      <MaterialReactTable table={table} />
    </Container>
  );
};

export default RASDSTsTable;
